<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import ReportHeader from './components/ReportHeader.vue';

const { t } = useI18n();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();

const availableLabels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');

// Same per-account key the "Label funnel" report reads from, so ordering the
// tags here drives the chart there (never leaks between accounts).
const funnelStorageKey = computed(
  () => `label_funnel_labels_${currentAccountId.value}`
);

const funnelLabels = ref([]);

const labelByTitle = computed(() => {
  const map = {};
  availableLabels.value.forEach(label => {
    map[label.title] = label;
  });
  return map;
});

const pickableLabels = computed(() =>
  availableLabels.value.filter(label => !funnelLabels.value.includes(label.title))
);

const colorFor = title => labelByTitle.value[title]?.color || '#94a3b8';

const persistFunnel = () => {
  updateUISettings({ [funnelStorageKey.value]: [...funnelLabels.value] });
};

const addStage = title => {
  funnelLabels.value.push(title);
  persistFunnel();
};

const removeStage = index => {
  funnelLabels.value.splice(index, 1);
  persistFunnel();
};

const moveStage = (index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= funnelLabels.value.length) return;
  const list = [...funnelLabels.value];
  [list[index], list[target]] = [list[target], list[index]];
  funnelLabels.value = list;
  persistFunnel();
};

onMounted(() => {
  store.dispatch('labels/get');
});

// Restore the saved funnel order once UI settings are available.
watch(
  () => uiSettings.value?.[funnelStorageKey.value],
  saved => {
    if (saved && !funnelLabels.value.length) {
      funnelLabels.value = [...saved];
    }
  },
  { immediate: true }
);
</script>

<template>
  <ReportHeader :header-title="t('LABEL_FUNNEL.STAGES_HEADER')" />

  <div class="flex flex-col gap-4 px-1 pb-8">
    <div
      class="flex flex-col gap-4 p-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <div class="flex flex-col gap-1">
        <h3 class="m-0 text-sm font-semibold text-n-slate-12">
          {{ t('LABEL_FUNNEL.STAGES_TITLE') }}
        </h3>
        <p class="m-0 text-xs text-n-slate-11">
          {{ t('LABEL_FUNNEL.STAGES_PAGE_HINT') }}
        </p>
      </div>

      <ul v-if="funnelLabels.length" class="flex flex-col gap-2 list-none">
        <li
          v-for="(title, index) in funnelLabels"
          :key="title"
          class="flex items-center gap-3 px-3 py-2 rounded-lg bg-n-alpha-2 border-l-4"
          :style="{ borderLeftColor: colorFor(title) }"
        >
          <span
            class="text-sm font-semibold tabular-nums text-n-slate-11 w-5 text-center"
          >
            {{ index + 1 }}
          </span>
          <span class="flex-1 text-sm text-n-slate-12">
            {{ title }}
          </span>
          <button
            type="button"
            class="p-1 rounded text-n-slate-10 hover:text-n-slate-12 disabled:opacity-30"
            :disabled="index === 0"
            :title="t('LABEL_FUNNEL.MOVE_UP')"
            @click="moveStage(index, -1)"
          >
            <span class="i-lucide-arrow-up size-4" />
          </button>
          <button
            type="button"
            class="p-1 rounded text-n-slate-10 hover:text-n-slate-12 disabled:opacity-30"
            :disabled="index === funnelLabels.length - 1"
            :title="t('LABEL_FUNNEL.MOVE_DOWN')"
            @click="moveStage(index, 1)"
          >
            <span class="i-lucide-arrow-down size-4" />
          </button>
          <button
            type="button"
            class="p-1 rounded text-n-slate-10 hover:text-n-ruby-11"
            :title="t('LABEL_FUNNEL.REMOVE')"
            @click="removeStage(index)"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </li>
      </ul>
      <p v-else class="m-0 text-sm text-n-slate-11">
        {{ t('LABEL_FUNNEL.EMPTY') }}
      </p>

      <div v-if="pickableLabels.length" class="flex flex-col gap-2 pt-1">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('LABEL_FUNNEL.ADD_LABEL') }}
        </span>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="label in pickableLabels"
            :key="label.id"
            type="button"
            class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border border-n-weak hover:bg-n-alpha-1 text-n-slate-12"
            @click="addStage(label.title)"
          >
            <span
              class="w-2 h-2 rounded-full"
              :style="{ backgroundColor: label.color }"
            />
            {{ label.title }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
